import { useState, useEffect, useRef } from "react";
import { Sparkles } from "lucide-react";
import { getLabHighlights, updateLabHighlights } from "../../apiCalls/user";
import "../../styling/highlights.css";

const MAX = 6;

const SUGGESTIONS = [
  "NABL Accredited",
  "Home Sample Collection",
  "24/7 Available",
  "Online Reports",
  "ISO Certified",
  "Senior Pathologists",
  "Same-Day Results",
  "Affordable Pricing",
  "Experienced Staff",
  "State-of-the-Art Equipment",
  "Digital Reports",
  "Walk-in Welcome",
];

const LabHighlights = ({ labId }) => {
  const [highlights, setHighlights] = useState([]);
  const [input,      setInput]      = useState("");
  const [isLoading,  setIsLoading]  = useState(true);
  const [isSaving,   setIsSaving]   = useState(false);
  const inputRef = useRef(null);

  // Fetch highlights from backend on mount
  useEffect(() => {
    const fetchHighlights = async () => {
      setIsLoading(true);
      const data = await getLabHighlights(labId);
      setHighlights(Array.isArray(data?.highlights) ? data.highlights : []);
      setIsLoading(false);
    };
    if (labId) fetchHighlights();
  }, [labId]);

  const isFull = highlights.length >= MAX;

  const addHighlight = (value) => {
    const trimmed = value.trim();
    if (!trimmed || isFull) return;
    if (highlights.some((h) => h.toLowerCase() === trimmed.toLowerCase())) return;
    setHighlights((prev) => [...prev, trimmed]);
    setInput("");
    inputRef.current?.focus();
  };

  const removeHighlight = (index) => {
    setHighlights((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addHighlight(input);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    await updateLabHighlights({ highlights }, labId);
    setIsSaving(false);
  };

  const suggestions = SUGGESTIONS.filter(
    (s) => !highlights.some((h) => h.toLowerCase() === s.toLowerCase())
  );

  return (
    <div className="form-card">
      <div className="form-card-header">
        <h5 className="form-card-title">
          <Sparkles size={15} /> Lab Highlights
        </h5>
        <span className={`hl-counter ${isFull ? "hl-counter--full" : ""}`}>
          {highlights.length} / {MAX}
        </span>
      </div>

      <div className="form-card-body">

        {/* ── Active tags ── */}
        <p className="hl-section-label">Active Highlights</p>

        <div className="hl-tags">
          {isLoading ? (
            <span className="hl-tags-empty">Loading highlights…</span>
          ) : highlights.length === 0 ? (
            <span className="hl-tags-empty">
              No highlights added yet — pick from suggestions below or type your own
            </span>
          ) : (
            highlights.map((tag, i) => (
              <span key={i} className="hl-tag">
                {tag}
                <button
                  className="hl-tag-remove"
                  type="button"
                  onClick={() => removeHighlight(i)}
                  aria-label={`Remove ${tag}`}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>

        {/* ── Input row ── */}
        <div className="hl-input-row">
          <input
            ref={inputRef}
            type="text"
            className="field-input"
            placeholder={
              isFull
                ? `Max ${MAX} highlights reached`
                : "Type a highlight and press Enter or click Add…"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isFull || isLoading}
            maxLength={60}
          />
          <button
            type="button"
            className="app-btn app-btn--primary app-btn--sm"
            onClick={() => addHighlight(input)}
            disabled={isFull || !input.trim() || isLoading}
          >
            Add
          </button>
        </div>

        {/* ── Suggestions ── */}
        {!isLoading && suggestions.length > 0 && !isFull && (
          <>
            <div className="hl-divider" />
            <p className="hl-suggestions-label">Or select from suggested highlights</p>
            <div className="hl-suggestions">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="hl-suggestion-pill"
                  onClick={() => addHighlight(s)}
                  disabled={isFull}
                >
                  <span className="hl-suggestion-pill-icon">+</span>
                  {s}
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── Footer ── */}
        <div className="hl-footer">
          <span className="hl-footer-hint">
            {isFull
              ? "Maximum reached. Remove a highlight to add another."
              : `You can add ${MAX - highlights.length} more highlight${MAX - highlights.length !== 1 ? "s" : ""}`}
          </span>
          <button
            type="button"
            className="app-btn app-btn--primary"
            onClick={handleSave}
            disabled={isSaving || isLoading}
          >
            {isSaving
              ? <><span className="svc-spinner" /> Saving…</>
              : <><Sparkles size={14} /> Save Highlights</>
            }
          </button>
        </div>

      </div>
    </div>
  );
};

export default LabHighlights;
